import { setDefaultSearchSourceCookie, setDynamicCopyCookie, } from '#pplx/frontend/apps/ask/browser-state/cookieManager.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode, isSearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { isWellKnownPromptSource, parseAttachmentsParam, } from '#pplx/frontend/comet/client/utils.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { isSourceType } from '#pplx/frontend/components/utils/SourcesConfig.js';
import { incrementCookieDefaultSearchQueryCount } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useGetPageStartTime } from '#pplx/frontend/metrics/providers/PerformanceProviderShared.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useLayoutEffect, useRef } from 'react';
function toSourcesArray(sources) {
    return sources?.length ? sources.filter(isSourceType) : undefined;
}
function toConfiguredSearchModel(model, isDynamicSearchModel) {
    if (!model || !isDynamicSearchModel(model)) {
        return undefined;
    }
    return model;
}
export const useSubmitOmniSearchQuery = (active, streamId, reason) => {
    // if streamable is undefined, we should submit the default query from client
    const streamIdRef = useRef(null);
    const { submitQuery } = usePerplexityQueryState();
    const router = useRouter();
    const getPageStartTime = useGetPageStartTime();
    const { getPreferredSearchModel } = usePreferredSearchModels();
    const { setConfiguredModel } = useThreadConfig();
    const { getDefaultModelForMode, getDefaultModelsForMode, isSearchModel: isDynamicSearchModel, query: searchModelsQuery, isLoaded: isSearchModelsLoaded, } = useSearchModels({
        reason,
        // Fail fast in the omnibox flow instead of waiting through React Query's
        // full retry backoff before we degrade to the placeholder model config.
        retry: 1,
    });
    const preferredSearchModel = getPreferredSearchModel(SearchMode.SEARCH);
    useLayoutEffect(() => {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const qParam = params.get('q');
        const utmSourceParam = params.get('utm_source');
        const inPageParam = params.get('in_page');
        const attachmentsParam = params.get('attachments');
        const tabsParam = params.get('tabs');
        const navSuggestionsParam = params.get('nav_suggestions');
        const sourcesParam = params.get('sources');
        const defaultSearchSource = params.get('pc');
        const dynamicCopyParam = params.get('pt');
        const modelParam = params.get('model');
        const searchModeParam = params.get('search_mode');
        if (defaultSearchSource) {
            setDefaultSearchSourceCookie(defaultSearchSource);
        }
        if (dynamicCopyParam) {
            setDynamicCopyCookie(dynamicCopyParam);
        }
        const quickActionButtonId = (params.get('qabi') ?? undefined);
        const attachments = parseAttachmentsParam(attachmentsParam);
        const newFrontendContextUUIDfromQ = params.get('newFrontendContextUUID');
        const mentions = [];
        if (tabsParam) {
            try {
                const tabs = JSON.parse(tabsParam);
                if (Array.isArray(tabs)) {
                    for (const tabId of tabs) {
                        mentions.push({
                            type: 'tab',
                            id: tabId,
                            url: '',
                        });
                    }
                }
            }
            catch (e) {
                logger.error('Failed to parse tabsParam:', e);
            }
        }
        /**
         * We only want to submit this default query
         * 1. once
         * 2. only if the user hasn't already submitted a query
         *
         * newFrontendContextUUIDfromQ - means that the query has already been submitted from other page
         * defaultQuerySubmitted - means that the default query has already been submitted from this page
         */
        if (active &&
            qParam &&
            !newFrontendContextUUIDfromQ &&
            streamId &&
            streamIdRef.current !== streamId) {
            const qFromParams = Array.isArray(qParam) ? qParam[0] : qParam;
            const sourceParam = params.get('source');
            // already submitted, aborting
            if (qFromParams === 'pending')
                return;
            const searchModeOverride = isSearchMode(searchModeParam)
                ? searchModeParam
                : SearchMode.SEARCH;
            const shouldWaitForSearchModels = (modelParam || searchModeOverride) && !isSearchModelsLoaded && !searchModelsQuery.isError;
            if (shouldWaitForSearchModels) {
                return;
            }
            const onThreadSlugReceived = (slug) => {
                // Use a closure to prevent switching to a different thread when multiple
                // sidebar query suggestions are submitted
                if (streamIdRef.current !== streamId)
                    return;
                const redirectUrl = utmSourceParam
                    ? `/search/${slug}?utm_source=${utmSourceParam}`
                    : `/search/${slug}`;
                router.replace(redirectUrl, 'Omni search query');
            };
            // If the models query errors, useSearchModels falls back to
            // MODEL_CONFIG_QUERY_PLACEHOLDER_DATA so validation still runs against
            // the static defaults instead of blocking the omnibox submission forever.
            const explicitModel = toConfiguredSearchModel(modelParam ?? undefined, isDynamicSearchModel);
            const rawDefaultModelForSearchMode = searchModeOverride
                ? getDefaultModelForMode(searchModeOverride)
                : undefined;
            const defaultModelForSearchMode = toConfiguredSearchModel(rawDefaultModelForSearchMode, isDynamicSearchModel);
            const modelPreferenceOverride = explicitModel ||
                defaultModelForSearchMode ||
                (searchModeOverride ? undefined : preferredSearchModel);
            const compareModelPreferencesOverride = searchModeOverride === SearchMode.AGENTIC_RESEARCH
                ? getDefaultModelsForMode(SearchMode.AGENTIC_RESEARCH)
                : undefined;
            if (explicitModel) {
                setConfiguredModel(explicitModel);
            }
            else if (defaultModelForSearchMode) {
                setConfiguredModel(defaultModelForSearchMode);
            }
            else if (searchModeOverride && rawDefaultModelForSearchMode) {
                logger.warn('Skipping invalid default search model for omnibox mode override', {
                    defaultModelForSearchMode: rawDefaultModelForSearchMode,
                    searchModeOverride,
                });
            }
            else {
                // When the omnibox submits without an explicit model param, reset
                // the configured model to the preferred search model. This prevents
                // modes like Computer/ASI selected on the NTP from leaking into the
                // omnibox submission — the omnibox always navigates to /search/new,
                // never to /computer/tasks, so the search mode must match.
                if (preferredSearchModel) {
                    setConfiguredModel(preferredSearchModel);
                }
            }
            submitQuery({
                rawQuery: qFromParams,
                collection: null,
                newFrontendContextUUID: safeRandomUUID(),
                frontendUUID: streamId,
                attachments,
                inPage: inPageParam ?? undefined,
                promptSource: isWellKnownPromptSource(sourceParam) ? sourceParam : 'user',
                // IMPORTANT: DO NOT USE querySource FROM SEARCH PARAMS
                // We use this param for security purposes. Omnisearch source will always be default_search.
                querySource: 'default_search',
                utmSource: utmSourceParam ?? undefined,
                quickActionButtonId,
                isNavSuggestionsDisabled: navSuggestionsParam === 'false',
                modelPreferenceOverride,
                compareModelPreferencesOverride,
                // When the omnibox does not carry an explicit model, force the search
                // mode to SEARCH so that ASI / Computer mode selected on the NTP
                // does not route the query to /computer/tasks.
                searchModeOverride: searchModeOverride ?? (explicitModel ? undefined : SearchMode.SEARCH),
                shouldRedirectToBackendUUID: false,
                mentions,
                sourcesOverride: sourcesParam ? toSourcesArray(sourcesParam?.split(',')) : undefined,
                shouldUsePageStartTime: true,
                onThreadSlugReceived,
            });
            streamIdRef.current = streamId;
            incrementCookieDefaultSearchQueryCount();
        }
    }, [
        active,
        submitQuery,
        streamId,
        getPageStartTime,
        router,
        reason,
        preferredSearchModel,
        getDefaultModelForMode,
        getDefaultModelsForMode,
        isDynamicSearchModel,
        isSearchModelsLoaded,
        searchModelsQuery.isError,
        setConfiguredModel,
    ]);
};
//# sourceMappingURL=useSubmitOmniSearchQuery.js.map